import axios from "axios";
import { globalCache, debounce } from "../utils/performance";
import { getCookie, clearCookie } from "../utilities/cookies";

const API_BASE_URL = "https://accounts.clikkle.com:5000/api";

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
});

const addAuthInterceptor = (client) => {
  client.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem("token");
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );
};

addAuthInterceptor(apiClient);

class ApiResponse {
  constructor(data, error = null, loading = false) {
    this.data = data;
    this.error = error;
    this.loading = loading;
    this.success = !error;
  }
}

const handleApiError = (error) => {
  if (error.response) {
    const { status, data } = error.response;
    return {
      message: data.message || data.error || "An error occurred",
      status,
      details: data.details || null,
    };
  } else if (error.request) {
    return {
      message: "Network error. Please check your connection.",
      status: 0,
      details: null,
    };
  } else {
    return {
      message: error.message || "An unexpected error occurred",
      status: 500,
      details: null,
    };
  }
};

export const userService = {
  async getProfile() {
    console.log("getProfile");
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      if (!user || !user._id) {
        throw new Error("No user found in localStorage");
      }

      const response = await apiClient.get(`/user/${user._id}`);
      return new ApiResponse(response.data);
    } catch (error) {
      const apiError = handleApiError(error);
      return new ApiResponse(null, apiError);
    }
  },

  async getUserById(userId) {
    try {
      const response = await apiClient.get(`/user/${userId}`);
      return new ApiResponse(response.data);
    } catch (error) {
      return new ApiResponse(null, handleApiError(error));
    }
  },

  async updateUser(userId, userData) {
    try {
      const response = await apiClient.put(`/user/${userId}`, userData);
      return new ApiResponse(response.data);
    } catch (error) {
      return new ApiResponse(null, handleApiError(error));
    }
  },

  async deleteUser(userId) {
    try {
      const response = await apiClient.delete(`/user/${userId}`);
      return new ApiResponse(response.data);
    } catch (error) {
      return new ApiResponse(null, handleApiError(error));
    }
  },

  async updatePreferences(userId, preferences) {
    try {
      const response = await apiClient.put(`/user/${userId}`, {
        preferences: preferences,
      });
      return new ApiResponse(response.data);
    } catch (error) {
      return new ApiResponse(null, handleApiError(error));
    }
  },

  async updateSecuritySettings(userId, securityData) {
    try {
      const response = await apiClient.put(`/user/${userId}`, {
        security: securityData,
      });
      return new ApiResponse(response.data);
    } catch (error) {
      return new ApiResponse(null, handleApiError(error));
    }
  },
};

export const healthService = {
  async checkHealth() {
    try {
      const response = await apiClient.get("/health");
      return response.data;
    } catch (error) {
      const apiError = handleApiError(error);
      throw new Error(apiError.message);
    }
  },
};

export const platformService = {
  async getPlatforms() {
    try {
      const response = await apiClient.get(
        "/platforms?sortBy=name&direction=1"
      );
      return new ApiResponse(response.data);
    } catch (error) {
      return new ApiResponse(null, handleApiError(error));
    }
  },
};

export const apiUtils = {
  async get(endpoint, config = {}) {
    try {
      const cacheKey = `GET_${endpoint}_${JSON.stringify(config.params || {})}`;
      const cached = globalCache.get(cacheKey);

      if (cached && !config.skipCache) {
        return cached;
      }

      const response = await apiClient.get(endpoint, config);
      const apiResponse = new ApiResponse(response.data);

      if (response.status === 200 && !config.skipCache) {
        globalCache.set(cacheKey, apiResponse, 5 * 60 * 1000);
      }

      return apiResponse;
    } catch (error) {
      return new ApiResponse(null, handleApiError(error));
    }
  },

  async post(endpoint, data, config = {}) {
    try {
      const response = await apiClient.post(endpoint, data, config);

      if (response.status >= 200 && response.status < 300) {
        this.invalidateCache(endpoint);
      }

      return new ApiResponse(response.data);
    } catch (error) {
      return new ApiResponse(null, handleApiError(error));
    }
  },

  async put(endpoint, data, config = {}) {
    try {
      const response = await apiClient.put(endpoint, data, config);

      if (response.status >= 200 && response.status < 300) {
        this.invalidateCache(endpoint);
      }

      return new ApiResponse(response.data);
    } catch (error) {
      return new ApiResponse(null, handleApiError(error));
    }
  },

  async delete(endpoint, config = {}) {
    try {
      const response = await apiClient.delete(endpoint, config);

      if (response.status >= 200 && response.status < 300) {
        this.invalidateCache(endpoint);
      }

      return new ApiResponse(response.data);
    } catch (error) {
      return new ApiResponse(null, handleApiError(error));
    }
  },

  invalidateCache(pattern) {
    const keys = Array.from(globalCache.cache.keys());
    keys.forEach((key) => {
      if (key.includes(pattern)) {
        globalCache.delete(key);
      }
    });
  },

  clearCache() {
    globalCache.clear();
  },
};

export { apiClient };

export { ApiResponse };
